if (!customElements.get('product-authors')) {
  customElements.define(
    'product-authors',
    class ProductAuthors extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-viewport]');
        this.prevButton = this.querySelector('[data-prev]');
        this.nextButton = this.querySelector('[data-next]');
        this.status = this.querySelector('[data-status]');
        if (!this.viewport || !this.prevButton || !this.nextButton) return;

        this.activeIndex = 0;
        this.lastWidth = this.viewport.clientWidth;

        this.onPrev = () => this.step(-1);
        this.onNext = () => this.step(1);
        this.onScroll = () => this.update();
        this.onKeydown = (event) => {
          if (event.target !== this.viewport) return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(1);
          }
        };

        this.prevButton.addEventListener('click', this.onPrev);
        this.nextButton.addEventListener('click', this.onNext);
        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
        this.viewport.addEventListener('keydown', this.onKeydown);

        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.viewport);
        this.update();
      }

      disconnectedCallback() {
        if (!this.viewport) return;
        this.prevButton.removeEventListener('click', this.onPrev);
        this.nextButton.removeEventListener('click', this.onNext);
        this.viewport.removeEventListener('scroll', this.onScroll);
        this.viewport.removeEventListener('keydown', this.onKeydown);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      get slides() {
        return Array.from(this.viewport.children);
      }

      // The card currently lined up with the left edge of the viewport.
      get currentIndex() {
        const edge = this.viewport.getBoundingClientRect().left;
        let nearest = 0;
        let nearestDistance = Infinity;
        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - edge);
          if (distance < nearestDistance) {
            nearest = index;
            nearestDistance = distance;
          }
        });
        return nearest;
      }

      // Distance to scroll to line a card up with the left edge of the viewport.
      offsetTo(index) {
        return this.slides[index].getBoundingClientRect().left - this.viewport.getBoundingClientRect().left;
      }

      step(direction) {
        const target = Math.min(Math.max(this.currentIndex + direction, 0), this.slides.length - 1);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.viewport.scrollBy({ left: this.offsetTo(target), behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      // When the cards change width (rotating a phone, resizing the window) the
      // browser can leave the scroll position on the wrong card, so put the
      // author who was in view back in view.
      onResize() {
        const width = this.viewport.clientWidth;
        if (width !== this.lastWidth) {
          this.lastWidth = width;
          this.viewport.scrollBy({ left: this.offsetTo(this.activeIndex), behavior: 'auto' });
        }
        this.update();
      }

      update() {
        const total = this.slides.length;
        // A scroll caused by a width change is not the reader moving to another
        // author: hold on to the card they were on until onResize has re-aligned.
        const resizing = this.viewport.clientWidth !== this.lastWidth;
        const index = resizing ? this.activeIndex : this.currentIndex;
        this.activeIndex = index;
        this.prevButton.disabled = index <= 0;
        this.nextButton.disabled = index >= total - 1;
        if (this.status) this.status.textContent = `${index + 1} / ${total}`;
      }
    }
  );
}
